import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { ExternalLink, Search, Wallet } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  formatUsd,
  formatPct,
  marginLabel,
} from '@/lib/jobCostingData';

const TONE_CLASSES = {
  success:
    'bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30',
  warning:
    'bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30',
  danger:
    'bg-[hsl(var(--destructive))]/15 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/30',
  // Time & Material — neutral brand tone, never a health verdict.
  tm: 'bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] border-[hsl(var(--primary))]/30',
};

const MarginBadge = ({ tone, pct, t }) => {
  // Map the existing string output of marginLabel() to translated forms.
  const raw = marginLabel(pct);
  const labelMap = { Healthy: 'healthy', Watch: 'watch', 'At Risk': 'risk' };
  const key = labelMap[raw] || 'watch';
  const localized = t(`jobCosting.marginLabels.${key}`, { defaultValue: raw });
  return (
    <Badge
      variant="outline"
      className={cn('uppercase text-[10px] tracking-[0.16em]', TONE_CLASSES[tone])}
    >
      {localized} {'\u00b7'} {formatPct(pct)}
    </Badge>
  );
};

const MarginBarCmp = ({ pct, tone }) => {
  const barBg = {
    success: 'bg-[hsl(var(--success))]',
    warning: 'bg-[hsl(var(--warning))]',
    danger: 'bg-[hsl(var(--destructive))]',
  }[tone];
  const clamped = Math.max(0, Math.min(100, Number(pct) || 0));
  return (
    <div className="h-1 w-full bg-[hsl(var(--muted))] rounded-full overflow-hidden">
      <div
        className={cn('h-full transition-all', barBg)}
        style={{ width: `${clamped}%` }}
      />
    </div>
  );
};

const Row = ({ row, onOpen }) => {
  const { t } = useTranslation();
  const budgetUsedPct =
    row.budget > 0 ? Math.round((row.actualCost / row.budget) * 100) : 0;
  return (
    <div
      className="grid grid-cols-12 gap-2 items-center px-3 py-3 border-b border-border hover:bg-[hsl(var(--muted))]/30 transition-colors cursor-pointer"
      onClick={() => onOpen(row)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(row);
        }
      }}
      role="button"
      tabIndex={0}
      data-testid={`costing-line-${row.job_id}`}
    >
      <div className="col-span-12 sm:col-span-3 min-w-0">
        <div className="text-sm font-semibold text-foreground truncate">
          {row.name}
        </div>
        <div className="text-xs text-muted-foreground truncate">
          {row.client || t('jobCosting.noClient')} {'\u00b7'} {row.status || t('jobCosting.unknown')}
        </div>
      </div>
      <div className="col-span-6 sm:col-span-2">
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          {t('jobCosting.table.colRevenue')}
        </div>
        <div className="text-sm font-semibold text-foreground tabular-nums">
          {formatUsd(row.revenue)}
        </div>
      </div>
      <div className="col-span-6 sm:col-span-2">
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          {t('jobCosting.table.colActual')}
        </div>
        <div className="text-sm font-semibold text-foreground tabular-nums">
          {formatUsd(row.actualCost)}
        </div>
        <div className="text-[11px] text-muted-foreground">
          {formatUsd(row.labor.cost)} {t('jobCosting.table.laborShort')}
          {row.labor.sub?.cost > 0
            ? ` (${t('jobCosting.table.inclSub', {
                defaultValue: 'incl. {{amount}} sub',
                amount: formatUsd(row.labor.sub.cost),
              })})`
            : ''}{' '}
          {'\u00b7'} {formatUsd(row.materials.total)} {t('jobCosting.table.materialsShort')}
        </div>
      </div>
      <div className="col-span-6 sm:col-span-2">
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          {t('jobCosting.table.colProfit')}
        </div>
        <div
          className={cn(
            'text-sm font-semibold tabular-nums',
            row.grossProfit < 0 && !row.is_tm
              ? 'text-[hsl(var(--destructive))]'
              : 'text-foreground'
          )}
        >
          {formatUsd(row.grossProfit)}
        </div>
        {row.is_tm && row.grossProfit < 0 ? (
          <div className="text-[11px] text-muted-foreground">
            {t('jobCosting.table.tmUnbilledNote', {
              defaultValue: 'work ahead of billing',
            })}
          </div>
        ) : null}
      </div>
      <div className="col-span-6 sm:col-span-3 space-y-1.5">
        {row.is_tm ? (
          <>
            <Badge
              variant="outline"
              className={cn('uppercase text-[10px] tracking-[0.16em]', TONE_CLASSES.tm)}
            >
              {t('jobCosting.table.tmBadge', { defaultValue: 'T&M' })}
            </Badge>
            <div className="text-[10px] text-muted-foreground tabular-nums">
              {t('jobCosting.table.tmBilled', {
                defaultValue: 'Billed to date: {{amount}}',
                amount: formatUsd(row.billed || 0),
              })}
            </div>
          </>
        ) : (
          <>
            <MarginBadge tone={row.tone} pct={row.marginPct} t={t} />
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <MarginBarCmp pct={row.marginPct} tone={row.tone} />
              </div>
              <span className="text-[10px] text-muted-foreground tabular-nums shrink-0">
                {t('jobCosting.table.budgetUsed', { pct: budgetUsedPct })}
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const FilterTabs = ({ value, onChange }) => {
  const { t } = useTranslation();
  return (
    <Tabs value={value} onValueChange={onChange} className="w-full sm:w-auto">
      <TabsList
        className="bg-[hsl(var(--muted))] border border-border w-full sm:w-auto overflow-x-auto no-scrollbar"
        data-testid="costing-status-tabs"
      >
        <TabsTrigger value="all" className="text-xs uppercase tracking-[0.16em]">
          {t('jobCosting.table.tabs.all')}
        </TabsTrigger>
        <TabsTrigger value="active" className="text-xs uppercase tracking-[0.16em]">
          {t('jobCosting.table.tabs.active')}
        </TabsTrigger>
        <TabsTrigger value="healthy" className="text-xs uppercase tracking-[0.16em]">
          {t('jobCosting.table.tabs.healthy')}
        </TabsTrigger>
        <TabsTrigger value="watch" className="text-xs uppercase tracking-[0.16em]">
          {t('jobCosting.table.tabs.watch')}
        </TabsTrigger>
        <TabsTrigger value="risk" className="text-xs uppercase tracking-[0.16em]">
          {t('jobCosting.table.tabs.risk')}
        </TabsTrigger>
      </TabsList>
    </Tabs>
  );
};

const TableSkeleton = () => (
  <div className="divide-y divide-border" data-testid="costing-skeleton">
    {Array.from({ length: 5 }).map((_, i) => (
      <div key={i} className="px-3 py-4 flex items-center gap-3">
        <Skeleton className="h-4 w-1/3" />
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-6 w-24 ml-auto" />
      </div>
    ))}
  </div>
);

export const JobCostingTable = ({
  rows,
  loading,
  statusFilter,
  onStatusFilter,
  search,
  onSearch,
  sort,
  onSort,
  onOpen,
}) => {
  const { t } = useTranslation();
  return (
    <Card
      className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]"
      data-testid="costing-table-card"
    >
      <CardContent className="p-3 sm:p-4">
        <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-3">
          <FilterTabs value={statusFilter} onChange={onStatusFilter} />
          <div className="flex flex-1 items-center gap-2">
            <div className="relative flex-1">
              <Search
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
                size={14}
              />
              <Input
                value={search}
                onChange={(e) => onSearch(e.target.value)}
                placeholder={t('jobCosting.table.searchPlaceholder')}
                className="pl-8 h-9 bg-[hsl(var(--muted))] border-border text-foreground"
                data-testid="costing-search"
              />
            </div>
            <Select value={sort} onValueChange={onSort}>
              <SelectTrigger
                className="h-9 w-[200px] bg-[hsl(var(--muted))] border-border"
                data-testid="costing-sort"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[hsl(var(--card))] border-border">
                <SelectItem value="margin_asc">{t('jobCosting.table.sort.marginAsc')}</SelectItem>
                <SelectItem value="margin_desc">{t('jobCosting.table.sort.marginDesc')}</SelectItem>
                <SelectItem value="revenue_desc">{t('jobCosting.table.sort.revenueDesc')}</SelectItem>
                <SelectItem value="actual_desc">{t('jobCosting.table.sort.actualDesc')}</SelectItem>
                <SelectItem value="name_az">{t('jobCosting.table.sort.nameAz')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="hidden sm:grid grid-cols-12 gap-2 px-3 py-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground border-b border-border">
          <span className="col-span-3">{t('jobCosting.table.colJobClient')}</span>
          <span className="col-span-2">{t('jobCosting.table.colRevenue')}</span>
          <span className="col-span-2">{t('jobCosting.table.colActual')}</span>
          <span className="col-span-2">{t('jobCosting.table.colProfit')}</span>
          <span className="col-span-3">{t('jobCosting.table.colMargin')}</span>
        </div>

        {loading ? (
          <TableSkeleton />
        ) : rows.length === 0 ? (
          <div
            className="py-16 text-center text-sm text-muted-foreground flex flex-col items-center gap-2"
            data-testid="costing-empty"
          >
            <Wallet size={20} className="text-muted-foreground/70" />
            {t('jobCosting.table.empty')}
          </div>
        ) : (
          <div data-testid="costing-list">
            {rows.map((row) => (
              <Row key={row.job_id} row={row} onOpen={onOpen} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default JobCostingTable;
